import { useCallback, useEffect, useState } from "react";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Users, Loader2, Link2, Search, Phone, Mail, MapPin, RefreshCw, Check } from "lucide-react";
import { toast } from "sonner";
import { getAgeingLoanParties, attachAgeingLoanParties, searchAgeingLoanParties } from "@/lib/api";

const KIND = {
  primary: { label: "Primary borrower", cls: "bg-blue-100 text-blue-800 border-blue-200" },
  co_borrower: { label: "Co-borrower · joint & several", cls: "bg-emerald-100 text-emerald-800 border-emerald-200" },
  ld_guarantor_record: { label: "LoanDisk guarantor record", cls: "bg-amber-100 text-amber-800 border-amber-200" },
  portal_contact: { label: "Known to portal only", cls: "bg-slate-100 text-slate-700 border-slate-200" },
  search: { label: "LoanDisk borrower", cls: "bg-slate-100 text-slate-700 border-slate-200" },
};

function PartyRow({ p, onAttach, attaching }) {
  const k = KIND[p.kind] || KIND.search;
  return (
    <li className="flex items-start gap-3 py-2 border-b last:border-b-0" data-testid="loan-party-row">
      <div className="flex-1 min-w-0">
        <div className="flex flex-wrap items-center gap-1.5">
          <span className="font-medium text-sm text-slate-900" data-testid="loan-party-name">{p.name}</span>
          <span className={`text-[10px] px-1.5 py-0.5 rounded border ${k.cls}`} data-testid="loan-party-kind">{k.label}{p.share_percent ? ` · ${p.share_percent}%` : ""}</span>
          {p.role && !["primary", "co_borrower"].includes(p.kind) && p.role !== k.label && <span className="text-[10px] text-slate-500">{p.role}</span>}
        </div>
        <div className="text-[11px] text-slate-600 flex flex-wrap gap-x-3 gap-y-0.5 mt-0.5">
          {p.business_name && <span>{p.business_name}</span>}
          {p.ld_borrower_id && <span className="font-mono text-slate-500" data-testid="loan-party-ld-id">LD borrower #{p.ld_borrower_id}</span>}
          {p.mobile && <span className="inline-flex items-center gap-0.5"><Phone className="w-2.5 h-2.5" />{p.mobile}</span>}
          {p.email && <span className="inline-flex items-center gap-0.5"><Mail className="w-2.5 h-2.5" />{p.email}</span>}
          {p.address && <span className="inline-flex items-center gap-0.5"><MapPin className="w-2.5 h-2.5" />{p.address}</span>}
        </div>
        {p.sources?.length > 0 && <div className="text-[10px] text-slate-400 mt-0.5">via {p.sources.join(" · ")}</div>}
      </div>
      {p.on_loan ? (
        <span className="text-[10px] text-emerald-700 inline-flex items-center gap-0.5 shrink-0 mt-1" data-testid="loan-party-on-loan"><Check className="w-3 h-3" /> on loan</span>
      ) : p.can_attach ? (
        <Button size="sm" variant="outline" className="h-7 text-[11px] shrink-0" disabled={attaching} onClick={() => onAttach([p.ld_borrower_id])} data-testid="loan-party-attach-btn">
          {attaching ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <Link2 className="w-3 h-3 mr-1" />} Attach as co-borrower
        </Button>
      ) : (
        <span className="text-[10px] text-slate-400 shrink-0 mt-1" data-testid="loan-party-no-ld-record">no LoanDisk record</span>
      )}
    </li>
  );
}

export function LoanPartiesCard({ caseId }) {
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  const [loading, setLoading] = useState(true);
  const [attaching, setAttaching] = useState(false);
  const [q, setQ] = useState("");
  const [searching, setSearching] = useState(false);
  const [found, setFound] = useState(null);

  const load = useCallback(async () => {
    setLoading(true); setErr(null);
    try { setData(await getAgeingLoanParties(caseId)); }
    catch (e) { setErr(e?.response?.data?.detail || e.message); }
    finally { setLoading(false); }
  }, [caseId]);
  useEffect(() => { load(); }, [load]);

  const attach = async (ids) => {
    setAttaching(true);
    try {
      const r = await attachAgeingLoanParties(caseId, ids);
      toast.success(`${ids.length} borrower${ids.length > 1 ? "s" : ""} attached to the LoanDisk loan as co-borrower${ids.length > 1 ? "s" : ""} — verified (${r.attached.length} on loan now)`);
      setFound(null); setQ("");
      await load();
    } catch (e) { toast.error("Attach failed: " + (e?.response?.data?.detail || e.message), { duration: 9000 }); }
    finally { setAttaching(false); }
  };

  const search = async () => {
    if (q.trim().length < 2) return;
    setSearching(true);
    try { setFound((await searchAgeingLoanParties(caseId, q.trim())).results || []); }
    catch (e) { toast.error(e?.response?.data?.detail || e.message); }
    finally { setSearching(false); }
  };

  const parties = data?.parties || [];
  const attachable = parties.filter((p) => p.can_attach && !p.on_loan);
  const onLoan = parties.filter((p) => p.on_loan).length;
  const onLoanIds = new Set(parties.filter((p) => p.on_loan).map((p) => p.ld_borrower_id));

  return (
    <Card data-testid="loan-parties-card">
      <CardHeader className="pb-2">
        <CardTitle className="text-sm flex items-center gap-2">
          <Users className="w-4 h-4" /> Borrowers on this loan
          {data && <span className="text-[11px] font-normal text-slate-500" data-testid="loan-parties-summary">{onLoan} on the LoanDisk loan · {parties.length - onLoan} known but not attached</span>}
          <Button variant="ghost" size="sm" className="h-6 w-6 p-0 ml-auto" onClick={load} disabled={loading} data-testid="loan-parties-refresh"><RefreshCw className={`w-3 h-3 ${loading ? "animate-spin" : ""}`} /></Button>
        </CardTitle>
      </CardHeader>
      <CardContent className="pt-0">
        {loading && !data && <div className="text-xs text-slate-500 flex items-center gap-1.5 py-2" data-testid="loan-parties-loading"><Loader2 className="w-3 h-3 animate-spin" /> Reading the loan from LoanDisk…</div>}
        {err && <div className="text-xs text-amber-700 py-2" data-testid="loan-parties-error">{err}</div>}
        {data && (
          <>
            <ul data-testid="loan-parties-list">
              {parties.map((p, i) => <PartyRow key={`${p.kind}-${p.ld_borrower_id || i}`} p={p} onAttach={attach} attaching={attaching} />)}
            </ul>
            {attachable.length > 1 && (
              <Button size="sm" className="mt-2 h-7 text-[11px]" disabled={attaching} onClick={() => attach(attachable.map((p) => p.ld_borrower_id))} data-testid="loan-parties-attach-all">
                {attaching ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <Link2 className="w-3 h-3 mr-1" />} Attach all {attachable.length} as co-borrowers
              </Button>
            )}
            <div className="mt-3 pt-2 border-t">
              <div className="text-[11px] text-slate-500 mb-1">Director missing above? Find their LoanDisk borrower record and attach it.</div>
              <div className="flex gap-1.5">
                <Input value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && search()} placeholder="Name or business…" className="h-7 text-xs" data-testid="loan-parties-search-input" />
                <Button size="sm" variant="outline" className="h-7 px-2" onClick={search} disabled={searching || q.trim().length < 2} data-testid="loan-parties-search-btn">
                  {searching ? <Loader2 className="w-3 h-3 animate-spin" /> : <Search className="w-3 h-3" />}
                </Button>
              </div>
              {found && (
                <ul className="mt-1.5 max-h-48 overflow-y-auto" data-testid="loan-parties-search-results">
                  {found.length === 0 && <li className="text-[11px] text-slate-500 py-1" data-testid="loan-parties-search-empty">No LoanDisk borrower matches “{q}”.</li>}
                  {found.map((p) => <PartyRow key={p.ld_borrower_id} p={{ ...p, on_loan: onLoanIds.has(p.ld_borrower_id) }} onAttach={attach} attaching={attaching} />)}
                </ul>
              )}
            </div>
            <div className="text-[10px] text-slate-400 mt-2">Co-borrowers are jointly &amp; severally liable on LoanDisk and show on the loan (up to 10, {data.co_borrower_slots_left} slots left). Attaching is verified by reading the loan back.</div>
          </>
        )}
      </CardContent>
    </Card>
  );
}
